import { signalStore, type, withState, withMethods, patchState } from '@ngrx/signals';
import { inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { map, pipe, switchMap, tap, EMPTY, catchError, of } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { TBookingSettings, TCancelBookingsByUnavailableSpace, TSpaceActivities, TUnavailableSpace, TValidateUnavailableSpace } from '../entities/booking.model';
import { BookingService } from '../infra/booking.service';
import { ErrorStore } from '@application/errors/data-access';
import { setPropError, setPropLoaded, setPropLoading, withReqState, setPropInit } from '@application/data-access';
import {
  addEntity,
  entityConfig,
  removeEntity,
  setEntities,
  updateEntity,
  withEntities
} from '@ngrx/signals/entities';
import { TActivities } from '../entities/settings.model';
import { tapResponse } from '@ngrx/operators';

export type TBookingStore = {
  currentSpaceActivities: TSpaceActivities | null;
  bookingSettings: TBookingSettings;
};

export const initialBookingStore: TBookingStore = {
  currentSpaceActivities: null,
  bookingSettings: null,
};

const configUnavailableSpace = entityConfig({
  entity: type<TUnavailableSpace>(),
  collection: 'unavailableSpace',
  selectId: (unavailableSpace: TUnavailableSpace) => unavailableSpace.UnavailableSpaceId
});

const configSpaceActivities = entityConfig({
  entity: type<TSpaceActivities>(),
  collection: 'spaceActivities',
  selectId: (activity: TSpaceActivities) => activity.SpaceId
});

const configActivities = entityConfig({
  entity: type<TActivities>(),
  collection: 'activities',
  selectId: (activity: TActivities) => activity.ActivityId
});

const configValidateUnavailableSpace = entityConfig({
  entity: type<TValidateUnavailableSpace>(),
  collection: 'validateUnavailableSpace',
  selectId: (validate: TValidateUnavailableSpace) => validate.unavailableSpaceId
});

const configCancelBookingsByUnavailableSpace = entityConfig({
  entity: type<TCancelBookingsByUnavailableSpace>(),
  collection: 'cancelBookingsByUnavailableSpace',
  selectId: (cancelBooking: TCancelBookingsByUnavailableSpace) => cancelBooking.unavailableSpaceId
});

export const BookingStore = signalStore(
  { providedIn: 'root' },
  withState<TBookingStore>(initialBookingStore),
  withEntities(configSpaceActivities),
  withEntities(configActivities),
  withEntities(configCancelBookingsByUnavailableSpace),
  withEntities(configValidateUnavailableSpace),
  withEntities(configUnavailableSpace),
  withMethods(
    (
      store,
      bookingService = inject(BookingService),
      errorStore = inject(ErrorStore),
    ) => {
      return {
        loadAllSpaceActivities: rxMethod<boolean | undefined | null | void>(pipe(
          tap(() => patchState(store, setPropLoading('loadAllSpaceActivities'))),
          switchMap((refresh) => {
            const spaceActivities = structuredClone(store?.spaceActivitiesEntities());
            const hasActivities = spaceActivities?.length > 0;

            if (hasActivities && !refresh) {
              patchState(store, setPropLoaded('loadAllSpaceActivities'));
              return EMPTY;
            }

            return bookingService.getAllSpaceActivities().pipe(
              tapResponse({
                next: (response) => {
                  patchState(
                    store,
                    setEntities(response, configSpaceActivities),
                    setPropLoaded('loadAllSpaceActivities')
                  );
                },
                error: (error: HttpErrorResponse) => {
                  // errorStore.setHttpError(error);
                  patchState(store, setPropError('loadAllSpaceActivities', error));
                }
              })
            );
          })
        )),

        loadSpaceActivitiesBySpaceId: rxMethod<string>(pipe(
          tap(() => patchState(store, setPropLoading('loadSpaceActivitiesBySpaceId'))),
          switchMap((spaceId) => bookingService.getSpaceActivitiesBySpaceId(spaceId).pipe(
            tapResponse({
              next: (response) => {
                patchState(
                  store,
                  { currentSpaceActivities: response },
                  setPropLoaded('loadSpaceActivitiesBySpaceId')
                );
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('loadSpaceActivitiesBySpaceId', error));
              }
            })
          ))
        )),

        addSpaceActivity: rxMethod<Partial<TSpaceActivities>>(pipe(
          tap(() => patchState(store, setPropLoading('addSpaceActivity'))),
          switchMap((newActivity) => {
            return bookingService.addSpaceActivity(newActivity).pipe(
              tapResponse({
                next: (response) => {
                  patchState(
                    store,
                    addEntity(response, configSpaceActivities),
                    setPropLoaded('addSpaceActivity')
                  );
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('addSpaceActivity', error));
                }
              })
            );
          })
        )),

        updateSpaceActivity: rxMethod<Partial<TSpaceActivities>>(pipe(
          tap(() => patchState(store, setPropLoading('updateSpaceActivity'))),
          switchMap((activityData) => {
            return bookingService.updateSpaceActivity(activityData).pipe(
              tapResponse({
                next: (response) => {
                  patchState(
                    store,
                    updateEntity(
                      { id: response.SpaceId, changes: response },
                      configSpaceActivities
                    ),
                    setPropLoaded('updateSpaceActivity')
                  );
                },
                error: (error: HttpErrorResponse) => {
                  patchState(store, setPropError('updateSpaceActivity', error));
                }
              }),
            );
          })
        )),

        removeSpaceActivity: rxMethod<{ spaceId: string, activitiesIds: string[] }>(pipe(
          tap(() => patchState(store, setPropLoading('removeSpaceActivity'))),
          map((spacesToDetach) => {
            const spaceActivities = structuredClone(store.spaceActivitiesEntities());
            const isolateSpace = spaceActivities.find(spaceActivity => spacesToDetach.spaceId === spaceActivity.SpaceId);
            isolateSpace.ActivitiesSpaces = isolateSpace.ActivitiesSpaces
              .filter(activity => !spacesToDetach.activitiesIds
                .find(TenantId => TenantId === activity.TenantId)
              );
            return isolateSpace;
          }),

          switchMap((updatedSpaceActivity: TSpaceActivities) => {
            return bookingService.updateSpaceActivity(updatedSpaceActivity).pipe(
              tapResponse({
                next: () => {
                  patchState(
                    store,
                    updateEntity(
                      { id: updatedSpaceActivity.SpaceId, changes: updatedSpaceActivity },
                      configSpaceActivities
                    ),
                    setPropLoaded('removeSpaceActivity')
                  );
                },
                error: (error: HttpErrorResponse) => {
                  patchState(store, setPropError('removeSpaceActivity', error));
                }
              })
            );
          })
        )),

        detachActivityFromSpace: rxMethod<{ activitySpaceId: string, spaceActivityId: string }>(pipe(
          tap(() => patchState(store, setPropLoading('detachActivityFromSpace'))),
          switchMap(({ activitySpaceId, spaceActivityId }) => {
            return bookingService.detachActivityFromSpace(activitySpaceId).pipe(
              tapResponse({
                next: () => {
                  const entityMap = store.spaceActivitiesEntityMap();
                  const activity = entityMap[spaceActivityId];

                  if (activity) {
                    const updatedActivity = {
                      ...activity,
                      ActivitiesSpaces: activity.ActivitiesSpaces.filter(
                        a => a.ActivitySpacesId !== activitySpaceId
                      )
                    };

                    patchState(
                      store,
                      updateEntity(
                        { id: spaceActivityId, changes: updatedActivity },
                        configSpaceActivities
                      ),
                      setPropLoaded('detachActivityFromSpace')
                    );
                  } else {
                    patchState(store, setPropLoaded('detachActivityFromSpace'));
                  }
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('detachActivityFromSpace', error));
                }
              })
            );
          })
        )),

        attachActivityToSpace: rxMethod<{ spaceId: string, activityId: string }>(pipe(
          tap(() => patchState(store, setPropLoading('attachActivityToSpace'))),
          switchMap(({ spaceId, activityId }) => {
            return bookingService.attachActivityToSpace(spaceId, activityId).pipe(
              tapResponse({
                next: (newActivitySpace) => {
                  const allActivities = store.spaceActivitiesEntities();
                  const spaceActivities = allActivities.filter(
                    entity => entity.SpaceId === spaceId
                  );

                  if (spaceActivities.length > 0) {
                    const activity = spaceActivities[0];
                    const updatedActivity = {
                      ...activity,
                      ActivitiesSpaces: [...activity.ActivitiesSpaces, newActivitySpace]
                    };

                    patchState(
                      store,
                      updateEntity(
                        { id: activity.SpaceId, changes: updatedActivity },
                        configSpaceActivities
                      ),
                      setPropLoaded('attachActivityToSpace')
                    );
                  } else {
                    patchState(store, setPropLoaded('attachActivityToSpace'));
                  }
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('attachActivityToSpace', error));
                }
              })
            );
          })
        )),

        moveActivityToSpace: rxMethod<{ activityId: string, newSpaceId: string, oldSpaceId: string }>(pipe(
          tap(() => patchState(store, setPropLoading('moveActivityToSpace'))),
          switchMap(({ activityId, newSpaceId, oldSpaceId }) => {
            return bookingService.moveActivityToSpace(activityId, newSpaceId).pipe(
              tapResponse({
                next: (updatedActivitySpace) => {
                  const allActivities = store.spaceActivitiesEntities();
                  const sourceSpaceActivities = allActivities.filter(
                    entity => entity.SpaceId === oldSpaceId
                  );

                  const targetSpaceActivities = allActivities.filter(
                    entity => entity.SpaceId === newSpaceId
                  );

                  if (sourceSpaceActivities.length > 0) {
                    const sourceActivity = sourceSpaceActivities[0];
                    const updatedSourceActivity = {
                      ...sourceActivity,
                      ActivitiesSpaces: sourceActivity.ActivitiesSpaces.filter(
                        a => a.ActivityId !== activityId
                      )
                    };

                    patchState(
                      store,
                      updateEntity(
                        { id: sourceActivity.SpaceId, changes: updatedSourceActivity },
                        configSpaceActivities
                      )
                    );
                  }

                  if (targetSpaceActivities.length > 0) {
                    const targetActivity = targetSpaceActivities[0];
                    const updatedTargetActivity = {
                      ...targetActivity,
                      ActivitiesSpaces: [...targetActivity.ActivitiesSpaces, updatedActivitySpace]
                    };

                    patchState(
                      store,
                      updateEntity(
                        { id: targetActivity.SpaceId, changes: updatedTargetActivity },
                        configSpaceActivities
                      )
                    );
                  }

                  patchState(store, setPropLoaded('moveActivityToSpace'));
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('moveActivityToSpace', error));
                }
              })
            );
          })
        )),

        loadBookingSettings: rxMethod<void>(pipe(
          tap(() => patchState(store, { ...setPropLoading('loadBookingSettings') })),
          switchMap(() => bookingService.getBookingSettings().pipe(
            tap((bookingSettings) => {
              patchState(
                store,
                { bookingSettings },
                setPropLoaded('loadBookingSettings')
              );
            }),
            catchError((error: HttpErrorResponse) => {
              patchState(store, { ...setPropError('loadBookingSettings', error) });
              return of(error);
            })
          ))
        )),

        updateBookingSettings: rxMethod<Partial<TBookingSettings>>(pipe(
          tap(() => patchState(store, setPropLoading('updateBookingSettings'))),
          switchMap((payload) => {
            return bookingService.updateBookingSettings(payload).pipe(
              tap((updatedSettings) => {
                patchState(
                  store,
                  { bookingSettings: updatedSettings },
                  setPropLoaded('updateBookingSettings')
                );
              }),
              catchError((error: HttpErrorResponse) => {
                patchState(store, setPropError('updateBookingSettings', error));
                return of(error);
              })
            );
          })
        )),

        loadUnavailableSpaces: rxMethod<void>(pipe(
          tap(() => patchState(store, setPropLoading('loadUnavailableSpaces'))),
          switchMap(() => bookingService.getUnavailableSpaces().pipe(
            tap((unavailableSpaces) => {
              patchState(
                store,
                setEntities(unavailableSpaces, configUnavailableSpace),
                setPropLoaded('loadUnavailableSpaces')
              );
            }),
            catchError((error: HttpErrorResponse) => {
              patchState(store, { ...setPropError('loadUnavailableSpaces', error) });
              return of(error);
            })
          ))
        )),

        removeUnavailableSchedule: rxMethod<{ unavailableSpace: Partial<TUnavailableSpace>, unavailableScheduleId: string }>(pipe(
          tap(() => patchState(store, { ...setPropLoading('removeUnavailableSchedule') })),
          switchMap(({ unavailableSpace, unavailableScheduleId }) => {
            const updatedSpace = {
              ...unavailableSpace,
              UnavailableSchedules: unavailableSpace.UnavailableSchedules.filter(
                schedule => schedule.UnavailableScheduleId !== unavailableScheduleId
              )
            };

            if (updatedSpace.UnavailableSchedules.length === 0) {
              return bookingService.removeUnavailableSpace(updatedSpace.UnavailableSpaceId).pipe(
                tap(() => {
                  patchState(
                    store,
                    removeEntity(updatedSpace.UnavailableSpaceId, configUnavailableSpace),
                    setPropLoaded('removeUnavailableSchedule')
                  );
                }),
                catchError((error: HttpErrorResponse) => {
                  patchState(store, { ...setPropError('removeUnavailableSchedule', error) });
                  return of(error);
                })
              );
            }

            return bookingService.updateUnavailableSpace(updatedSpace).pipe(
              tap((response) => {
                patchState(
                  store,
                  updateEntity(
                    { id: response.UnavailableSpaceId, changes: response },
                    configUnavailableSpace
                  ),
                  setPropLoaded('removeUnavailableSchedule')
                );
              }),
              catchError((error: HttpErrorResponse) => {
                patchState(store, { ...setPropError('removeUnavailableSchedule', error) });
                return of(error);
              })
            );
          })
        )),

        saveUnavailableSchedule: rxMethod<Partial<TUnavailableSpace>>(pipe(
          tap(() => patchState(store, { ...setPropLoading('saveUnavailableSchedule') })),
          switchMap((unavailableSpace) => {

            const existingSpace = store.unavailableSpaceEntities().find(item => item.SpaceId === unavailableSpace.SpaceId);
            if (existingSpace) {
              if (!unavailableSpace.UnavailableSpaceId) {
                unavailableSpace = {
                  ...existingSpace,
                  UnavailableSchedules: [
                    ...existingSpace.UnavailableSchedules,
                    ...unavailableSpace.UnavailableSchedules
                  ]
                };
              }
              return bookingService.updateUnavailableSpace(unavailableSpace).pipe(
                tap((response) => {
                  patchState(
                    store,
                    updateEntity(
                      { id: response.UnavailableSpaceId, changes: response },
                      configUnavailableSpace
                    ),
                    setPropLoaded('saveUnavailableSchedule')
                  );
                }),
                catchError((error: HttpErrorResponse) => {
                  patchState(store, { ...setPropError('saveUnavailableSchedule', error) });
                  return of(error);
                })
              );
            }
            return bookingService.addUnavailableSpace(unavailableSpace).pipe(
              tap((response) => {
                patchState(
                  store,
                  addEntity(response, configUnavailableSpace),
                  setPropLoaded('saveUnavailableSchedule')
                );
              }),
              catchError((error: HttpErrorResponse) => {
                patchState(store, { ...setPropError('saveUnavailableSchedule', error) });
                return of(error);
              })
            );
          })

        )),

        getAllActivities: rxMethod<boolean | undefined | null | void>(pipe(
          tap(() => patchState(store, { ...setPropLoading('getAllActivities') })),
          switchMap((refresh) => {
            const hasActivities = store.activitiesEntities().length > 0;
            if (hasActivities && !refresh) {
              patchState(store, { ...setPropLoaded('getAllActivities') });
              return EMPTY;
            }
            return bookingService.getAllActivities().pipe(
              tap((response) => {
                patchState(
                  store,
                  setEntities(response?.value, configActivities),
                  setPropLoaded('loadAllSpaceActivities')
                );

              }),
              catchError((error: HttpErrorResponse) => {
                patchState(store, { ...setPropError('getAllActivities', error) });
                return of(error);
              })
            )
          })
        )),

        validateUnavailableSpace: rxMethod<Partial<TUnavailableSpace>>(pipe(
          tap(() => patchState(store, { ...setPropLoading('validateUnavailableSpace') })),
          switchMap((unavailableSpace) => {
            return bookingService.validateUnavailableSpace(unavailableSpace).pipe(
              tap((isValid) => {
                const validate: TValidateUnavailableSpace = {
                  unavailableSpaceId: unavailableSpace.UnavailableSpaceId,
                  isValid
                }
                if (store.validateUnavailableSpaceEntityMap()[validate.unavailableSpaceId]) {
                  patchState(
                    store,
                    updateEntity(
                      { id: validate.unavailableSpaceId, changes: validate },
                      configValidateUnavailableSpace
                    ),
                    setPropLoaded('validateUnavailableSpace')
                  );
                  return;
                }
                patchState(
                  store,
                  addEntity(validate, configValidateUnavailableSpace),
                  setPropLoaded('validateUnavailableSpace')
                );

              }),
              catchError((error: HttpErrorResponse) => {
                patchState(store, { ...setPropError('validateUnavailableSpace', error) });
                return of(error);
              })
            );
          })
        )),

        cancelBookingsByUnavailableSpace: rxMethod<Partial<TUnavailableSpace>>(pipe(
          tap(() => patchState(store, { ...setPropLoading('cancelBookingsByUnavailableSpace') })),
          switchMap((unavailableSpace) => {
            return bookingService.cancelBookingsByUnavailableSpace(unavailableSpace).pipe(
              tap(() => {
                const cancelBooking: TCancelBookingsByUnavailableSpace = {
                  unavailableSpaceId: unavailableSpace.UnavailableSpaceId,
                  isCancelled: true
                };
                if (store.cancelBookingsByUnavailableSpaceEntityMap()[cancelBooking.unavailableSpaceId]) {
                  patchState(
                    store,
                    updateEntity(
                      { id: cancelBooking.unavailableSpaceId, changes: cancelBooking },
                      configCancelBookingsByUnavailableSpace
                    ),
                    setPropLoaded('cancelBookingsByUnavailableSpace')
                  );
                  return;
                }
                patchState(
                  store,
                  addEntity(cancelBooking, configCancelBookingsByUnavailableSpace),
                  setPropLoaded('cancelBookingsByUnavailableSpace')
                );
              }),
              catchError((error: HttpErrorResponse) => {
                patchState(store, { ...setPropError('cancelBookingsByUnavailableSpace', error) });
                return of(error);
              })
            );
          })
        )),

        blankStateTo(propNames: string[]) {
          patchState(store, setPropInit(...propNames));
        },
      };
    }
  ),
  withReqState(
    'loadAllSpaceActivities',
    'loadSpaceActivitiesBySpaceId',
    'addSpaceActivity',
    'updateSpaceActivity',
    'removeSpaceActivity',
    'removeActivities',
    'detachActivityFromSpace',
    'attachActivityToSpace',
    'moveActivityToSpace',
    'loadBookingSettings',
    'updateBookingSettings',
    'loadUnavailableSpaces',
    'removeUnavailableSchedule',
    'saveUnavailableSchedule',
    'getAllActivities',
    'cancelBookingsByUnavailableSpace',
    'validateUnavailableSpace'
  )
);
